import { Pipe, PipeTransform } from '@angular/core';
import { DateTime, Duration } from 'luxon';
import { FeUtil } from '../../hezky-routes';

@Pipe({
  name: 'relativeTime',
  pure: true,
})
export class RelativeTimePipe implements PipeTransform {
  constructor(private feUtil: FeUtil) {}
  /**
   * Returns a human-readable relative time string with maximum accuracy,
   * handling different timezones and all time units down to seconds.
   */
  transform(
    value: string | number | null,
    format: 'card' | 'card-relative' | 'modal' | 'default' = 'default'
  ): string {
    if (!value && value !== 0) return 'N/A';

    const labels = {
      year: format === 'card' || format === 'card-relative' ? 'Y' : 'year',
      month: format === 'card' || format === 'card-relative' ? 'M' : 'month',
      day: format === 'card' || format === 'card-relative' ? 'D' : 'day',
      hour: format === 'card' || format === 'card-relative' ? 'H' : 'hour',
      minute: format === 'card' || format === 'card-relative' ? 'M' : 'minute'
    };
  
    // ---- NEW: detect milliseconds duration ----
    if (typeof value === 'number' && value < 1000 * 60 * 60 * 24 * 365 * 50 && value !== 0) {
      // treat as duration, not timestamp
      // Account for days and months as well
      const duration = Duration.fromMillis(value).shiftTo(
        'months',
        'days',
        'hours',
        'minutes',
        'seconds'
      );

      const mo = Math.floor(duration.months ?? 0);
      const d = Math.floor(duration.days ?? 0);
      const h = Math.floor(duration.hours ?? 0);
      const m = Math.floor(duration.minutes ?? 0);
      const s = Math.floor(duration.seconds ?? 0);

      if (mo === 0 && d === 0 && h === 0 && m === 0 && s === 0) return 'Just now';

      const parts = [];
      if (mo) parts.push(`${mo}${format === 'card' || format === 'card-relative' ? 'mo' : ` ${mo === 1 ? 'month' : 'months'}`}`);
      if (d) parts.push(`${d}${format === 'card' || format === 'card-relative' ? 'd' : ` ${d === 1 ? 'day' : 'days'}`}`);
      if (h) parts.push(`${h}${format === 'card' || format === 'card-relative' ? 'h' : ` ${h === 1 ? 'hour' : 'hours'}`}`);
      if (m) parts.push(`${m}${format === 'card' || format === 'card-relative' ? 'm' : ` ${m === 1 ? 'minute' : 'minutes'}`}`);
      // Only include seconds if it's the only non-zero part
      if (s && parts.length === 0) parts.push(`${s}${format === 'card' || format === 'card-relative' ? 's' : ' seconds'}`);

      let result = parts.join(format === 'card' ? ' ' : ' ');

      if (format === 'default') return result + ' ago';
      if (format === 'modal') return result + ' ago';
      if (format === 'card-relative') return result + ' ago';
      if (format === 'card') return result;

      return result;
    }
  
    // ---- existing DATE-TIME logic from here ----
    let timeZone = this.feUtil.time_zone || DateTime.local().zoneName;
  
    let propDate: DateTime | null = null;
  
    if (typeof value === 'number') {
      propDate = DateTime.fromMillis(value, { zone: timeZone });
    } else {
      if (DateTime.fromSQL(value, { zone: timeZone }).isValid) {
        propDate = DateTime.fromSQL(value, { zone: timeZone });
      } else if (DateTime.fromISO(value, { zone: timeZone }).isValid) {
        propDate = DateTime.fromISO(value, { zone: timeZone });
      } else if (!isNaN(Date.parse(value))) {
        propDate = DateTime.fromJSDate(new Date(value), { zone: timeZone });
      }
    }
  
    if (!propDate || !propDate.isValid) return 'Invalid date';
  
    const now = DateTime.now().setZone(timeZone);
    const isFuture = propDate > now;
  
    let diffDuration: Duration = isFuture
      ? propDate.diff(now, ['years', 'months', 'days', 'hours', 'minutes'])
      : now.diff(propDate, ['years', 'months', 'days', 'hours', 'minutes']);
  
    diffDuration = diffDuration.shiftTo(
      'years',
      'months',
      'days',
      'hours',
      'minutes'
    );
  

    const units = ['year', 'month', 'day', 'hour', 'minute'] as const;
    let resultParts: string[] = [];
  
    for (const unit of units) {
      const v = Math.floor(diffDuration.get(unit) || 0);
      if (v > 0) {
        let label = labels[unit];
        if (format !== 'card' && format !== 'card-relative' && v > 1) label += 's';
        resultParts.push(`${v}${label.length > 1 ? ' ' : ''}${label}`);
      }
    }
  
    let result = resultParts.join(' ').trim();
    if (!result) result = isFuture ? 'In a moment' : 'Just now';
  
    if (format === 'card') {
      result = `${isFuture ? 'In ' : ''}${result}`;
    } else if (format === 'card-relative') {
      result = isFuture
        ? `In ${result?.toLowerCase()} ago`
        : result?.toLowerCase() === 'just now'
          ? 'Just now'
          : `${result?.toLowerCase()} ago`;
    } else if (format === 'modal') {
      result = `${isFuture ? 'In ' : ''}${result}${isFuture ? ' from now' : ''}`;
    } else if (format === 'default') {
      if (result !== 'Just now') result += isFuture ? '' : ' ago';
    }
    return result;
  }
}
